// Helpers for wiring chart-segment clicks into the drill-down turbo-frame.
// Used by chart_controller.js; kept here to isolate drilldown concerns from
// the chart controller's core chart-config plumbing.

import { filterBarParams } from './analytics_filters'

const DRILLDOWN_FRAME_ID = 'ai-chart-drilldown'

const LOADING_HTML = '<div class="dashboard-card mt-4 flex items-center justify-center py-8">' +
                     '<div class="animate-pulse text-xs text-gray-400">Loading drill-down…</div></div>'

// A line on top of a bar chart is an aggregate sum across stack segments — its dataset name
// is not a filterable category value. We still drill, but without the dataset param so the
// defensive `({{clicked_dataset}} = '' OR ...)` SQL pattern short-circuits to a no-op.
function isAggregateOverlay(chart, datasetIndex) {
  const datasetType = chart.getDatasetMeta(datasetIndex)?.type
  return datasetType === 'line' && chart.config.type !== 'line'
}

// A dashboard being rearranged: a click on a tile is the start of a move, not a drill.
function isRearranging(event) {
  return Boolean(event.native?.target?.closest?.('.dashboard-grid-editing'))
}

// Returns a Chart.js options fragment with onClick/onHover handlers.
// `getDrilldownUrl` is a () => string callback so the latest value is read at click time
// (Stimulus value updates after construction stay live without re-wiring).
export function drilldownInteractionConfig(getDrilldownUrl) {
  const onClick = (event, _active, chart) => {
    const drilldownUrl = getDrilldownUrl()
    if (!drilldownUrl || isRearranging(event)) return
    const points = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true)
    if (!points.length) return
    const { datasetIndex, index } = points[0]
    const label = chart.data.labels?.[index]
    const dataset = chart.data.datasets?.[datasetIndex]?.label
    const url = new URL(drilldownUrl, window.location.origin)
    if (label != null) url.searchParams.set('label', label)
    if (dataset && !isAggregateOverlay(chart, datasetIndex)) url.searchParams.set('dataset', dataset)
    filterBarParams(url.searchParams)
    const frame = document.getElementById(DRILLDOWN_FRAME_ID)
    if (frame) {
      frame.innerHTML = LOADING_HTML
      frame.src = url.toString()
      // The dashboard shows the frame in a modal, which opens on this.
      window.dispatchEvent(new CustomEvent('chart-drilldown:open'))
    }
  }
  const onHover = (event, _active, chart) => {
    if (!getDrilldownUrl() || isRearranging(event)) return
    const points = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true)
    event.native.target.style.cursor = points.length ? 'pointer' : 'default'
  }
  return { options: { onClick, onHover } }
}
